import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH16_QUESTIONS } from "../data/neetPhysicsCh16Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 16: Electric Charges and Fields (NCERT Class 12, Ch. 1).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Electric Charge Basics", "Coulomb's Law & Superposition", "Electric Field & Field Lines", "Electric Dipole", "Gauss's Law & Applications"];

const SECTION_COLORS = {
  "Electric Charge Basics":            "#2563eb",
  "Coulomb's Law & Superposition":     "#7c3aed",
  "Electric Field & Field Lines":      "#dc2626",
  "Electric Dipole":                   "#ea580c",
  "Gauss's Law & Applications":        "#0d9488",
};

const CONCEPTS = [
  // ── Electric Charge Basics ──────────────────────────────────────────────────
  { section: "Electric Charge Basics", title: "Electric charge — basic properties",
    explanation: "Electric charge is an intrinsic property of matter that comes in exactly two kinds, historically labelled positive and negative. Like charges (+,+ or -,-) repel each other; unlike charges (+,-) attract. Charge is measured in coulombs (C) in SI units, and unlike mass, charge can be positive, negative, or zero (a neutral body has equal amounts of both, or none at all).",
    example: `Like charges  -> repel      (+,+) or (-,-)
Unlike charges -> attract   (+,-)

SI unit of charge: coulomb (C)
1 C is a very large charge for everyday electrostatics --
typical charges produced by rubbing (e.g. a comb on hair)
are of the order of microcoulombs (10^-6 C) or less.` },
  { section: "Electric Charge Basics", title: "Quantization of electric charge",
    explanation: "Charge cannot take just any value -- it is always an integral multiple of the elementary charge e = 1.6 x 10^-19 C, the smallest unit of free charge found in nature (the charge on an electron is -e, on a proton is +e). So any observable charge q obeys q = n.e, where n is an integer (n = 0, ±1, ±2, ...). At the macroscopic level this graininess is invisible because n is enormous, so charge appears to vary continuously.",
    example: `q = n.e ,  e = 1.6 x 10^-19 C

A charged body carries q = -1.6 x 10^-18 C.
Find n:  n = q / e = (1.6 x 10^-18) / (1.6 x 10^-19) = 10
So the body has an excess of exactly 10 electrons.` },
  { section: "Electric Charge Basics", title: "Conservation and additivity of electric charge",
    explanation: "Conservation of charge: the total charge of an isolated system stays constant -- charge can be transferred between bodies but never created or destroyed. Additivity of charge: the total charge of a system is simply the algebraic (scalar) sum of all the charges present, taking sign into account -- unlike vector quantities, no angles or directions are involved.",
    example: `Two identical isolated spheres carry +6 uC and -2 uC.
Total charge (additivity): +6 + (-2) = +4 uC

They are brought into contact and separated. Being identical,
they share the total charge equally:
Each sphere now carries: +4 uC / 2 = +2 uC

Check (conservation): +2 + +2 = +4 uC  <- same as before contact` },
  { section: "Electric Charge Basics", title: "Conductors and insulators",
    explanation: "Conductors (e.g. metals, the human body, earth) contain large numbers of free (mobile) charge carriers -- typically free electrons -- that move readily through the material, so charge given to a conductor spreads over its entire surface almost instantly. Insulators (e.g. glass, rubber, plastic) have no such free charges; charge placed on one part stays localized there and does not flow.",
    example: `Conductor: touch a charged metal rod at one end
           -> charge instantly spreads over the WHOLE rod

Insulator: touch a charged plastic rod at one end
           -> charge stays put near the point of contact only` },
  { section: "Electric Charge Basics", title: "Charging by conduction and induction",
    explanation: "Charging by CONDUCTION requires direct contact: a charged body touches a neutral one, and some charge (of the SAME sign) flows onto it until potentials equalize. Charging by INDUCTION requires no contact: a charged rod is brought near a neutral conductor, polarizing it (opposite charge accumulates on the near side, like charge on the far side); grounding the far side and then removing the ground (while the rod is still nearby) leaves the conductor with a net charge OPPOSITE to that of the inducing rod.",
    example: `Conduction: charged rod (+) touches neutral sphere
            -> sphere ends up with SAME sign charge (+), reduced magnitude

Induction:  charged rod (+) brought NEAR neutral sphere (no contact)
            -> near side becomes (-), far side becomes (+)
            -> ground the far side, remove ground, then remove rod
            -> sphere is left with net (-) charge -- OPPOSITE to the rod` },

  // ── Coulomb's Law & Superposition ───────────────────────────────────────────
  { section: "Coulomb's Law & Superposition", title: "Coulomb's law — statement and formula",
    explanation: "Coulomb's law states that the electrostatic force between two point charges q1 and q2 separated by a distance r acts along the line joining them, is directly proportional to the product of the charges, and inversely proportional to the square of the distance between them: F = k.q1.q2/r^2, where k = 1/(4.pi.epsilon0) ~ 9 x 10^9 N.m^2/C^2 in vacuum/air. Like charges give a repulsive force; unlike charges give an attractive force.",
    example: `F = k q1 q2 / r^2 ,  k ~ 9 x 10^9 N.m^2/C^2

q1 = 2 uC, q2 = 3 uC, r = 0.3 m
F = (9x10^9)(2x10^-6)(3x10^-6) / (0.3)^2
F = (9x10^9)(6x10^-12) / 0.09 = 0.054 / 0.09 = 0.6 N   (repulsive)` },
  { section: "Coulomb's Law & Superposition", title: "Permittivity of free space and effect of a dielectric medium",
    explanation: "The constant epsilon0 (permittivity of free space) = 8.85 x 10^-12 C^2/N.m^2 sets the strength of electrostatic interactions in vacuum, with k = 1/(4.pi.epsilon0). When charges are placed in a dielectric medium instead of vacuum, the force is reduced by a factor equal to the medium's dielectric constant (relative permittivity) K: F_medium = F_vacuum / K. Since K > 1 for all real dielectrics, the force is always weaker in a medium than in vacuum for the same charges and separation.",
    example: `F_vacuum = 0.6 N   (from previous example)
Same charges now placed in water, K(water) = 80

F_medium = F_vacuum / K = 0.6 / 80 = 7.5 x 10^-3 N` },
  { section: "Coulomb's Law & Superposition", title: "Coulomb's law in vector form",
    explanation: "In vector form, the force on charge q1 due to q2 is F21 = k.q1.q2/r^2 . r-hat21, where r-hat21 is the unit vector pointing from q2 to q1. This automatically gives a force along the line joining the charges, repulsive (along +r-hat) when q1.q2 > 0 and attractive (along -r-hat) when q1.q2 < 0. By Newton's third law, F12 = -F21.",
    example: `F21 (force on charge 1 due to charge 2) = k q1 q2 / r^2 . r-hat21
F12 (force on charge 2 due to charge 1) = k q1 q2 / r^2 . r-hat12 = -F21

If q1, q2 same sign  -> F21 points along +r-hat21 (pushes 1 away from 2)
If q1, q2 opposite sign -> F21 points along -r-hat21 (pulls 1 toward 2)` },
  { section: "Coulomb's Law & Superposition", title: "Principle of superposition of electrostatic forces",
    explanation: "When more than two charges are present, the net force on any one charge is the VECTOR SUM of the individual Coulomb forces exerted on it by each of the other charges taken one at a time -- each pairwise force is completely unaffected by the presence of the other charges. For charges arranged along a single line, this reduces to an algebraic sum of forces (with sign/direction).",
    example: `q0 = +1 uC at origin.
q1 = +2 uC at x = +0.02 m  -> repels q0 in the -x direction
q2 = +3 uC at x = -0.03 m  -> repels q0 in the +x direction

F1 = k q0 q1 / (0.02)^2 = (9x10^9)(1e-6)(2e-6)/(4x10^-4) = 45 N  (-x)
F2 = k q0 q2 / (0.03)^2 = (9x10^9)(1e-6)(3e-6)/(9x10^-4) = 30 N  (+x)

Net force = F2 - F1 = 30 - 45 = -15 N, i.e. 15 N in the -x direction` },

  // ── Electric Field & Field Lines ────────────────────────────────────────────
  { section: "Electric Field & Field Lines", title: "Electric field — definition",
    explanation: "The electric field at a point is the force experienced per unit positive test charge placed at that point, in the limit that the test charge q0 is small enough not to disturb the source charges: E = F/q0. Electric field is a vector, points in the direction of the force on a POSITIVE test charge, and has SI unit newton per coulomb (N/C), equivalent to volt per metre (V/m).",
    example: `E = F / q0

A test charge q0 = 2 x 10^-9 C placed at a point experiences
a force F = 3 x 10^-4 N.

E = F / q0 = (3x10^-4) / (2x10^-9) = 1.5 x 10^5 N/C` },
  { section: "Electric Field & Field Lines", title: "Electric field due to a point charge",
    explanation: "The electric field at distance r from an isolated point charge Q has magnitude E = k.Q/r^2 = Q/(4.pi.epsilon0.r^2), directed radially OUTWARD from Q if Q is positive, and radially INWARD (toward Q) if Q is negative. Notice this is exactly the Coulomb force per unit charge -- E = F/q0 = kQq0/r^2 / q0 = kQ/r^2.",
    example: `E = k Q / r^2

Q = 5 uC, r = 0.2 m
E = (9x10^9)(5x10^-6) / (0.2)^2 = (4.5x10^4) / 0.04 = 1.125 x 10^6 N/C
directed radially away from Q (since Q is positive)` },
  { section: "Electric Field & Field Lines", title: "Electric field due to a system of point charges",
    explanation: "For several point charges, the net electric field at any point is the VECTOR SUM of the fields due to each individual charge, calculated as if the others were absent -- the same superposition principle used for forces, just applied to E instead of F.",
    example: `Q1 = +4 uC at x = 0,  Q2 = +1 uC at x = 1 m
Find net field at the midpoint, x = 0.5 m

E1 = k Q1 / (0.5)^2 = (9x10^9)(4x10^-6)/0.25 = 1.44 x 10^5 N/C  (+x, away from Q1)
E2 = k Q2 / (0.5)^2 = (9x10^9)(1x10^-6)/0.25 = 3.6  x 10^4 N/C  (-x, away from Q2)

Net E = 1.44x10^5 - 0.36x10^5 = 1.08 x 10^5 N/C, directed from Q1 toward Q2` },
  { section: "Electric Field & Field Lines", title: "Electric field lines and their properties",
    explanation: "Field lines are an imaginary visual aid: the tangent to a field line at any point gives the direction of E there, and the density of lines (number per unit cross-sectional area) represents the field's magnitude. Key properties: field lines start on positive charges (or infinity) and end on negative charges (or infinity); they never cross each other (E would be undefined at a crossing); they are closer together where the field is stronger; and for a static configuration, field lines meet a conductor's surface at right angles.",
    example: `Near a single (+) charge : lines radiate straight OUTWARD, symmetric
Near a single (-) charge : lines converge straight INWARD, symmetric
Between (+) and (-) charges (a dipole): lines leave (+), curve
   across, and enter (-) -- denser in the region between the charges
Two field lines NEVER intersect` },

  // ── Electric Dipole ──────────────────────────────────────────────────────────
  { section: "Electric Dipole", title: "Electric dipole and dipole moment",
    explanation: "An electric dipole is a pair of equal and opposite point charges (+q and -q) separated by a small distance 2a. Its dipole moment p is a vector of magnitude p = q x (2a), directed from the negative charge to the positive charge, with SI unit coulomb-metre (C.m). Dipole moment measures how 'lopsided' the charge separation is.",
    example: `p = q . (2a)

q = 2 uC = 2x10^-6 C,  separation 2a = 1 cm = 0.01 m

p = (2x10^-6)(0.01) = 2 x 10^-8 C.m,  directed from -q to +q` },
  { section: "Electric Dipole", title: "Electric field on the axial line of a dipole",
    explanation: "On the axial line (the line through both charges, extended), for a point at distance r from the dipole's centre with r much greater than a, the field magnitude is E_axial ~ 2.k.p / r^3, directed along the dipole moment p (i.e. parallel to it, from the -q side toward the +q side).",
    example: `E_axial = 2 k p / r^3     (for r >> a)

p = 2 x 10^-8 C.m (from previous example), r = 0.1 m (a = 0.5 cm, so r >> a holds)

E_axial = 2(9x10^9)(2x10^-8) / (0.1)^3 = 360 / 0.001 = 3.6 x 10^5 N/C` },
  { section: "Electric Dipole", title: "Electric field on the equatorial line of a dipole",
    explanation: "On the equatorial (broadside-on) line -- the perpendicular bisector of the line joining the charges -- the field magnitude for r >> a is E_equatorial ~ k.p/r^3, directed ANTI-parallel to the dipole moment p (i.e. opposite to p's direction). At the same distance r, the axial field is exactly TWICE the equatorial field: E_axial = 2 . E_equatorial.",
    example: `E_equatorial = k p / r^3     (for r >> a)

Using the same p = 2 x 10^-8 C.m, r = 0.1 m:
E_equatorial = (9x10^9)(2x10^-8) / (0.1)^3 = 180 / 0.001 = 1.8 x 10^5 N/C

Check: E_axial / E_equatorial = (3.6x10^5) / (1.8x10^5) = 2   <- as expected` },
  { section: "Electric Dipole", title: "Torque on an electric dipole in a uniform field",
    explanation: "When a dipole of moment p is placed in a uniform external electric field E, the two equal and opposite forces (qE each, on +q and -q) form a couple that tends to align p with E. The torque is tau = p x E, with magnitude tau = p.E.sin(theta), where theta is the angle between p and E. Torque is maximum (tau = pE) when p is perpendicular to E, and zero when p is parallel or antiparallel to E.",
    example: `tau = p E sin(theta)

p = 2 x 10^-8 C.m,  E = 5 x 10^5 N/C,  theta = 30 deg

tau = (2x10^-8)(5x10^5)(sin30) = (0.01)(0.5) = 5 x 10^-3 N.m

Maximum possible torque (theta = 90 deg): tau_max = pE = 1 x 10^-2 N.m` },
  { section: "Electric Dipole", title: "Potential energy of an electric dipole in a uniform field",
    explanation: "The potential energy of a dipole in a uniform field is U = -p.E = -p.E.cos(theta). U is MINIMUM (most negative, U = -pE) when the dipole is aligned WITH the field (theta = 0, stable equilibrium), MAXIMUM (U = +pE) when anti-aligned (theta = 180 deg, unstable equilibrium), and zero when p is perpendicular to E (theta = 90 deg).",
    example: `U = -p E cos(theta)

p = 2 x 10^-8 C.m,  E = 5 x 10^5 N/C

theta = 0 deg (aligned):    U = -(0.01)(1)    = -1 x 10^-2 J  (minimum, stable)
theta = 60 deg:             U = -(0.01)(0.5)  = -5 x 10^-3 J
theta = 90 deg:             U = -(0.01)(0)    = 0 J
theta = 180 deg (opposed):  U = -(0.01)(-1)   = +1 x 10^-2 J  (maximum, unstable)` },

  // ── Gauss's Law & Applications ────────────────────────────────────────────────
  { section: "Gauss's Law & Applications", title: "Continuous charge distributions",
    explanation: "Real charged objects have charge spread over their volume, not concentrated at points, so it's described using density: linear charge density lambda = dq/dl (C/m) for charge along a line/wire; surface charge density sigma = dq/dA (C/m^2) for charge spread over a surface; and volume charge density rho = dq/dV (C/m^3) for charge filling a volume. Integrating these densities over the relevant length/area/volume gives the total charge.",
    example: `lambda = dq/dl (C/m)   sigma = dq/dA (C/m^2)   rho = dq/dV (C/m^3)

A thin rod of length L = 0.5 m carries a total charge Q = 5 uC,
uniformly distributed along its length.

lambda = Q / L = (5x10^-6) / 0.5 = 1 x 10^-5 C/m = 10 uC/m` },
  { section: "Gauss's Law & Applications", title: "Electric flux",
    explanation: "Electric flux through a surface measures the 'amount' of electric field passing through it: for a flat area A in a uniform field E, phi = E.A.cos(theta), where theta is the angle between E and the outward normal to the surface. For a general (non-flat, non-uniform) surface, phi = the surface integral of E.dA. SI unit of flux is N.m^2/C (equivalently V.m).",
    example: `phi = E A cos(theta)

E = 200 N/C, A = 0.5 m^2, angle between E and the surface normal = 60 deg

phi = (200)(0.5)(cos60) = (200)(0.5)(0.5) = 50 N.m^2/C` },
  { section: "Gauss's Law & Applications", title: "Gauss's law — statement and why it works",
    explanation: "Gauss's law states that the total electric flux through any CLOSED surface (a 'Gaussian surface') equals the total charge enclosed divided by epsilon0: phi_E = q_enclosed / epsilon0 -- regardless of the surface's shape or size, and regardless of charges OUTSIDE the surface (which contribute zero net flux). It works because the field of a point charge falls off as 1/r^2 while the area of a sphere around it grows as r^2, so their product (and hence the flux through any enclosing surface, by solid-angle arguments) is independent of r -- Gauss's law is a direct, more powerful restatement of Coulomb's law's inverse-square nature.",
    example: `phi_E = q_enclosed / epsilon0

A closed surface encloses a net charge Q = 8.85 x 10^-9 C
(epsilon0 = 8.85 x 10^-12 C^2/N.m^2)

phi_E = (8.85x10^-9) / (8.85x10^-12) = 1000 N.m^2/C

(true regardless of the surface's shape, or where inside it Q sits)` },
  { section: "Gauss's Law & Applications", title: "Gauss's law application: infinitely long charged wire",
    explanation: "For an infinitely long straight wire with uniform linear charge density lambda, symmetry demands E point radially outward (or inward) and be equal in magnitude everywhere on a coaxial cylindrical Gaussian surface of radius r. Applying Gauss's law to this cylinder gives E.(2.pi.r.l) = (lambda.l)/epsilon0, so E = lambda / (2.pi.epsilon0.r) = 2.k.lambda / r -- the field falls off as 1/r (slower than a point charge's 1/r^2).",
    example: `E = lambda / (2 pi epsilon0 r) = 2 k lambda / r

lambda = 2 x 10^-6 C/m,  r = 0.05 m

E = 2(9x10^9)(2x10^-6) / 0.05 = 3.6x10^4 / 0.05 = 7.2 x 10^5 N/C
directed radially outward (since lambda > 0)` },
  { section: "Gauss's Law & Applications", title: "Gauss's law application: infinite plane sheet of charge",
    explanation: "For an infinite plane sheet with uniform surface charge density sigma, symmetry demands E point perpendicular to the sheet, equal in magnitude on both sides, and constant with distance from the sheet. A Gaussian 'pillbox' straddling the sheet gives E = sigma / (2.epsilon0) -- notably INDEPENDENT of distance from the sheet. (Note: this is different from the field just outside a charged CONDUCTOR's surface, which is sigma/epsilon0 -- twice as large -- because a conductor's own surface charge only pushes field out one side, into the conductor's exterior.)",
    example: `E = sigma / (2 epsilon0)

sigma = 4 x 10^-6 C/m^2

E = (4x10^-6) / (2 x 8.85x10^-12) = (4x10^-6)/(1.77x10^-11) ~ 2.26 x 10^5 N/C
(same magnitude at 1 cm or 1 km from the sheet -- field doesn't fall off)` },
  { section: "Gauss's Law & Applications", title: "Gauss's law application: uniformly charged thin spherical shell",
    explanation: "For a thin spherical shell of radius R carrying total charge Q spread uniformly over its surface: OUTSIDE the shell (r > R), a spherical Gaussian surface of radius r encloses all of Q, giving E = k.Q/r^2 -- identical to a point charge Q at the centre. INSIDE the shell (r < R), a Gaussian sphere of radius r encloses ZERO charge (all the charge sits on the outer shell), so E = 0 everywhere inside. Right at the surface (r = R), E = k.Q/R^2 = sigma/epsilon0.",
    example: `Shell: R = 0.1 m, total charge Q = 2 x 10^-8 C

Outside, r = 0.2 m (r > R):
E = k Q / r^2 = (9x10^9)(2x10^-8) / (0.2)^2 = 180 / 0.04 = 4.5 x 10^3 N/C

At the surface, r = R = 0.1 m:
E = k Q / R^2 = 180 / 0.01 = 1.8 x 10^4 N/C

Inside, r = 0.05 m (r < R):
E = 0   (zero charge enclosed by a Gaussian sphere of this radius)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh16Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh16Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh16Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH16_QUESTIONS : NEET_PHYSICS_CH16_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 16</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Electric Charges and Fields</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 1 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH16_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH16_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
